import { useEffect, useRef, useState } from 'react';
import ScriptSureIframe from './ScriptSureIframe';
import { useParams } from 'react-router-dom';
import { Button, Input, Modal, Spin } from 'antd';
import {
    useGetPatientDetailsQuery,
    usePostPatientGetHelpMutation,
    usePostPatientScriptSureCookieMutation,
} from '../../features/patient/patientService';
import { setCredentials } from '../../features/auth/authSlice';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

const { TextArea } = Input;

const ScriptSureIframeLayout = ({
    getHelpModalOpen,
    setGetHelpModalOpen,
    scriptSureSource,
    setScriptSureWebAppUrl,
    triggerFunction = () => {}
}: {
    getHelpModalOpen: boolean;
    setGetHelpModalOpen: (value: boolean) => void;
    setScriptSureWebAppUrl?: (value: string) => void;
    scriptSureSource: (
        webAppUrl: string,
        scriptSurePatientId: number,
        scriptSureSessionToken: string,
    ) => string;
    triggerFunction?: () => void;
}) => {
    const dispatch = useDispatch();

    const { id: patientId = '' } = useParams();
    const { data: patientDetails } = useGetPatientDetailsQuery({
        id: patientId,
    });

    const [patientScriptSureCookie] = usePostPatientScriptSureCookieMutation();
    const [patientGetHelp] = usePostPatientGetHelpMutation();

    const [sourceUrl, setSourceUrl] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [helpDescriptionValue, setHelpDescriptionValue] =
        useState<string>('');

    const bac = bindActionCreators({ setCredentials }, dispatch);
    const [scriptSureLoading, setScriptSureLoading] = useState<boolean>(true);

    const getScriptSureData = async (SessionToken: any, PatientId: any) => {
        try {
            const response = await patientScriptSureCookie({
                SessionToken,
                PatientId,
            });
            return response;
        } catch (error) {
            console.log({ error });
        }
    };

    useEffect(() => {
        const redirectScriptSureUrl = async () => {
            try {
                setScriptSureLoading(true);
                let loginUserData: any = {};
                const localToken: any = localStorage.getItem('local-token');
                if (localToken !== null) {
                    loginUserData = JSON.parse(localToken);
                }
                const scriptSurePatientId: number | undefined =
                    patientDetails?.scriptSurePatientId;
                if (!scriptSurePatientId) {
                    alert(
                        'ePrescribe Authentication for Patient failed. Patient updates may still be processing. Try reloading the patient again if ePrescribe is failing.',
                    );
                    setScriptSureLoading(false);
                    return null;
                }

                const loggedUserPatientId: string = patientId;
                const loggedUserScriptSureSessionToken: string =
                    loginUserData.profile.scriptSure.sessiontoken;

                const response: any = await getScriptSureData(
                    loggedUserScriptSureSessionToken,
                    loggedUserPatientId,
                );

                const scriptSureSessionToken: string =
                    response.data.sessiontoken;
                const webAppUrl: string = response.data.webAppUrl;
                if (setScriptSureWebAppUrl) setScriptSureWebAppUrl(webAppUrl);

                // Updates session token if expired.
                if (
                    loginUserData.profile.scriptSure.sessiontoken !==
                    response.data.sessiontoken
                ) {
                    loginUserData.profile.scriptSure = response.data;
                    bac.setCredentials(loginUserData);
                }

                setSourceUrl(
                    scriptSureSource(
                        webAppUrl,
                        scriptSurePatientId,
                        scriptSureSessionToken,
                    ),
                );
            } catch (error) {
                console.log(error);
                setScriptSureLoading(false);
                return undefined;
            }
        };

        if (patientDetails) redirectScriptSureUrl();
    }, [patientDetails]);

    const submitHelp = async () => {
        setLoading(true);
        try {
            let loginUserData: any = {};
            const localToken: any = localStorage.getItem('local-token');
            if (localToken !== null) {
                loginUserData = JSON.parse(localToken);
            }

            const sentData = {
                ErrorDescription: {
                    SessionToken:
                        loginUserData?.profile?.scriptSure?.sessiontoken,
                    WidgetDetails: sourceUrl,
                    Notes:
                        helpDescriptionValue === ''
                            ? null
                            : helpDescriptionValue,
                },
                HospiceOrganizationId: patientDetails?.hospiceOrganizationId,
                PatientId: patientId,
            };
            if (!window.navigator.onLine) {
                withReactContent(Swal).fire({
                    icon: 'error',
                    title: 'You are Offline',
                    text: 'Please check your internet connection',
                    confirmButtonColor: 'rgb(33,150,243)',
                });
                closeGetHelpModal();
                return;
            }
            const response = await patientGetHelp(sentData);
            if (!response.hasOwnProperty('error')) {
                withReactContent(Swal).fire({
                    text: 'Request submitted successfully',
                    icon: 'success',
                    confirmButtonColor: 'rgb(33,150,243)',
                });
            } else {
                withReactContent(Swal).fire({
                    icon: 'error',
                    text: 'Support request failed',
                    confirmButtonColor: 'rgb(33,150,243)',
                });
            }
            closeGetHelpModal();
        } catch (error) {
            closeGetHelpModal();
            withReactContent(Swal).fire({
                icon: 'error',
                text: 'Support request failed',
            });
            console.log({ error });
        }
    };

    const closeGetHelpModal = () => {
        setLoading(false);
        setGetHelpModalOpen(false);
        setHelpDescriptionValue('');
    };

    return (
        <div>
            {scriptSureLoading && (
                <Spin spinning={scriptSureLoading} size="large" />
            )}
            {sourceUrl && (
                <ScriptSureIframe
                    open={true}
                    sourceUrl={sourceUrl}
                    setScriptSureLoading={setScriptSureLoading}
                    triggerFunction={triggerFunction}
                />
            )}
            {getHelpModalOpen && (
                <Modal
                    open={getHelpModalOpen}
                    closable={false}
                    footer={[
                        <Button
                            key="gotoallergy"
                            disabled={loading}
                            type="primary"
                            className="cancel-btn"
                            onClick={() => closeGetHelpModal()}
                        >
                            {'Cancel'}
                        </Button>,
                        <Button
                            key="Submit"
                            disabled={loading}
                            type="primary"
                            className="submit-help"
                            onClick={() => submitHelp()}
                        >
                            {'Submit'}
                        </Button>,
                    ]}
                >
                    <Spin spinning={loading} size="large">
                        <div className="display-flex">
                            <p className="para-styles">Get Help</p>
                        </div>
                        <div className="error-btn m-1 mb-3">
                            Please provide a brief description of the issue you
                            are facing with the e-prescribe functionality
                        </div>
                        <div>
                            <TextArea
                                rows={4}
                                placeholder="Description"
                                value={helpDescriptionValue}
                                onChange={(e) =>
                                    setHelpDescriptionValue(e.target.value)
                                }
                            />
                        </div>
                    </Spin>
                </Modal>
            )}
        </div>
    );
};

export default ScriptSureIframeLayout;
